import { ref } from 'vue'
import apiInstance from '@/utils/api'

// ===== CLOUD PRINTER INTERFACES =====
export interface CloudPrinter {
  id: number
  name: string
  mac_address: string
  order_mode: number
  status: number
  table_start: number
  table_end: number
  order_combine_time_period: number | string
  last_online: string
  error_key: string
  categories: string[]
}

export interface CloudPrinterResponse {
  error: boolean
  message: string
  data: CloudPrinter[]
}

export interface Category {
  id: number
  name: string
  status: number
}

export interface CategoryResponse {
  error: boolean
  message: string
  data: Category[]
}

export const useCloudPrinters = () => {
  const printers = ref<CloudPrinter[]>([])
  const isLoading = ref(false)
  const error = ref<string | null>(null)

  /**
   * Fetch all cloud printers for a restaurant
   */
  const fetchPrinters = async (restaurantId: string): Promise<void> => {
    isLoading.value = true
    error.value = null

    try {
      const response = await apiInstance.get<CloudPrinterResponse>(
        `/restaurant/${restaurantId}/cloud-printer`
      )

      if (response.data.error === false && response.data.data) {
        printers.value = response.data.data
      } else {
        error.value = response.data.message || 'Failed to fetch printers'
        printers.value = []
      }
    } catch (err: any) {
      error.value = err?.response?.data?.message || err?.message || 'Failed to fetch printers'
      printers.value = []
      console.error('Error fetching cloud printers:', err)
    } finally {
      isLoading.value = false
    }
  }

  /**
   * Delete a cloud printer
   */
  const deletePrinter = async (restaurantId: string, printerId: number): Promise<boolean> => {
    try {
      const response = await apiInstance.delete(
        `/restaurant/${restaurantId}/cloud-printer/${printerId}`
      )

      if (!response.data.error) {
        // Remove from local state
        printers.value = printers.value.filter(p => p.id !== printerId)
        return true
      } else {
        error.value = response.data.message || 'Failed to delete printer'
        return false
      }
    } catch (err: any) {
      error.value = err?.response?.data?.message || err?.message || 'Failed to delete printer'
      console.error('Error deleting cloud printer:', err)
      return false
    }
  }

  /**
   * Update a cloud printer
   */
  const updatePrinter = async (
    restaurantId: string,
    printerId: number,
    data: Partial<CloudPrinter>
  ): Promise<CloudPrinter | null> => {
    try {
      const response = await apiInstance.put(
        `/restaurant/${restaurantId}/cloud-printer/${printerId}`,
        data
      )

      if (!response.data.error && response.data.data) {
        // Update in local state
        const index = printers.value.findIndex(p => p.id === printerId)
        if (index !== -1) {
          printers.value[index] = response.data.data
        }
        return response.data.data
      } else {
        error.value = response.data.message || 'Failed to update printer'
        return null
      }
    } catch (err: any) {
      const errorMessage = err?.response?.data?.message || err?.response?.data?.errors || err?.message || 'Failed to update printer'
      error.value = typeof errorMessage === 'string' ? errorMessage : JSON.stringify(errorMessage)
      console.error('Error updating cloud printer:', {
        status: err?.response?.status,
        data: err?.response?.data,
        message: err?.message,
      })
      // Throw the error so it can be caught by the caller (form)
      throw err
    }
  }

  /**
   * Create a new cloud printer
   */
  const createPrinter = async (restaurantId: string, data: Omit<CloudPrinter, 'id' | 'last_online' | 'error_key'>): Promise<CloudPrinter | null> => {
    try {
      const response = await apiInstance.post(
        `/restaurant/${restaurantId}/cloud-printer`,
        data
      )

      if (!response.data.error && response.data.data) {
        printers.value.push(response.data.data)
        return response.data.data
      } else {
        error.value = response.data.message || 'Failed to create printer'
        return null
      }
    } catch (err: any) {
      const errorMessage = err?.response?.data?.message || err?.response?.data?.errors || err?.message || 'Failed to create printer'
      error.value = typeof errorMessage === 'string' ? errorMessage : JSON.stringify(errorMessage)
      console.error('Error creating cloud printer:', {
        status: err?.response?.status,
        data: err?.response?.data,
        message: err?.message,
      })
      // Throw the error so it can be caught by the caller (form)
      throw err
    }
  }

  /**
   * Fetch categories for a printer based on order mode and table configuration
   */
  const fetchCategories = async (restaurantId: string, payload: {
    cloud_printer_id?: number
    order_mode: number
    table_start?: number
    table_end?: number
  }): Promise<Category[]> => {
    try {
      const response = await apiInstance.post<CategoryResponse>(
        `/restaurant/${restaurantId}/cloud-printer/categories`,
        payload
      )

      if (!response.data.error && response.data.data) {
        return response.data.data
      } else {
        error.value = response.data.message || 'Failed to fetch categories'
        return []
      }
    } catch (err: any) {
      error.value = err?.response?.data?.message || err?.message || 'Failed to fetch categories'
      console.error('Error fetching categories:', err)
      return []
    }
  }

  /**
   * Reset printers list
   */
  const reset = () => {
    printers.value = []
    isLoading.value = false
    error.value = null
  }

  return {
    // State
    printers,
    isLoading,
    error,

    // Methods
    fetchPrinters,
    deletePrinter,
    updatePrinter,
    createPrinter,
    fetchCategories,
    reset,
  }
}
